import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import {
  ClientsiteAdminLoggerService,
  CustomErrors,
  ToastContent,
  WebsiteCustomField,
  WebsiteCustomFieldOption,
} from '@rx-applications/clientsite/admin/utils';
import { LoadStatus } from '@rx-applications/shared/utils';
import { switchMap, tap, Observable, withLatestFrom, filter, delay } from 'rxjs';
import { CustomFieldsBackendService } from '../backend/custom-field.backend';

interface CustomFieldsEditState {
  customField: WebsiteCustomField | null;
  fetchCustomFieldLoading: LoadStatus;
  error: HttpErrorResponse | null;
  updateOptionsStatus: LoadStatus;
  updateOptionsError: HttpErrorResponse | null;
  updateRequiredStatus: LoadStatus;
  updateRequiredError: HttpErrorResponse | null;
  tempOptionsState: {
    touched: boolean;
    options: Array<WebsiteCustomFieldOption>;
  };
  tempMandatoryState: {
    touched: boolean;
    value: boolean;
  };
}

const initialState: CustomFieldsEditState = {
  customField: null,
  fetchCustomFieldLoading: 'NOT_LOADED',
  updateOptionsStatus: 'NOT_LOADED',
  updateRequiredStatus: 'NOT_LOADED',
  error: null,
  updateOptionsError: null,
  updateRequiredError: null,
  tempOptionsState: {
    touched: false,
    options: [],
  },
  tempMandatoryState: {
    touched: false,
    value: false,
  },
};

@Injectable()
export class CustomFieldEditStore extends ComponentStore<CustomFieldsEditState> {
  #customFieldsBackendService = inject(CustomFieldsBackendService);
  #adminLoggerService = inject(ClientsiteAdminLoggerService);

  constructor() {
    super(initialState);
  }

  readonly loading$ = this.select((state) => state.fetchCustomFieldLoading);
  readonly isUpdatingCustomField$ = this.select(
    ({ updateOptionsStatus, updateRequiredStatus }) =>
      updateOptionsStatus === 'LOADING' || updateRequiredStatus === 'LOADING',
  );

  readonly toastContent$: Observable<{
    type: string;
    message: string;
  } | null> = this.select(({ updateRequiredStatus, updateOptionsStatus, updateRequiredError, updateOptionsError }) =>
    this.getToastContent(updateRequiredStatus, updateOptionsStatus, updateRequiredError, updateOptionsError),
  );

  readonly error$ = this.select((state) => state.error);
  readonly customField$ = this.select((state) => state.customField);
  readonly tempOptionsState$ = this.select(({ tempOptionsState }) => tempOptionsState);
  readonly tempMandatoryState$ = this.select(({ tempMandatoryState }) => tempMandatoryState);

  readonly previewOptions$ = this.select(({ customField, tempOptionsState }) =>
    tempOptionsState.touched ? tempOptionsState.options : customField?.options || [],
  );

  readonly previewMandatory$ = this.select(({ customField, tempMandatoryState }) =>
    tempMandatoryState.touched ? tempMandatoryState.value : !!customField?.required,
  );

  readonly errorMessageKey$ = this.select(({ error }) =>
    error?.error.title === CustomErrors.CustomFieldNotFound ? 'error.notFound' : 'error.unexpected',
  );

  readonly saveButtonEnabled$ = this.select(
    ({ tempMandatoryState, tempOptionsState }) => tempMandatoryState.touched || tempOptionsState.touched,
  );

  readonly updateRequiredStatus$ = this.select(({ updateRequiredStatus }) => updateRequiredStatus);
  readonly updateRequiredError$ = this.select(({ updateRequiredError }) => updateRequiredError);

  resetState(): void {
    this.setState(initialState);
  }

  resetUpdateStatus(): void {
    this.patchState((state) => ({
      ...state,
      updateOptionsStatus: initialState.updateOptionsStatus,
      updateRequiredStatus: initialState.updateOptionsStatus,
      updateOptionsError: null,
      updateRequiredError: null,
    }));
  }

  resetTemporaryState(): void {
    this.patchState((state) => ({
      ...state,
      tempMandatoryState: initialState.tempMandatoryState,
      tempOptionsState: initialState.tempOptionsState,
    }));
  }

  updateTempOptionsState = this.updater((state, options: Array<WebsiteCustomFieldOption>) => ({
    ...state,
    updateOptionsStatus: 'NOT_LOADED',
    updateOptionsError: null,
    tempOptionsState: { touched: true, options },
  }));

  updateTempMandatoryState = this.updater((state, value: boolean) => ({
    ...state,
    updateRequiredStatus: 'NOT_LOADED',
    updateRequiredError: null,
    tempMandatoryState: { touched: true, value },
  }));

  readonly fetchWebsiteCustomFieldById = this.effect((customFieldId$: Observable<string>) =>
    customFieldId$.pipe(
      tap(() => this.loadingCustomFieldById()),
      switchMap((customFieldId) => this.#customFieldsBackendService.getCustomFieldById(customFieldId)),
      tapResponse(
        (customField: WebsiteCustomField) => this.handleCustomFieldByIdSucceeded(customField),
        (error: HttpErrorResponse) => this.handleCustomFieldByIdFailed(error),
      ),
    ),
  );

  private loadingCustomFieldById = this.updater(() => ({ ...initialState, fetchCustomFieldLoading: 'LOADING' }));

  private handleCustomFieldByIdSucceeded = this.updater((state, customField: WebsiteCustomField) => ({
    ...state,
    fetchCustomFieldLoading: 'LOADED',
    customField,
  }));

  private handleCustomFieldByIdFailed = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    fetchCustomFieldLoading: 'ERROR',
    error,
  }));

  readonly setCustomFieldRequired = this.effect((request$: Observable<string>) =>
    request$.pipe(
      withLatestFrom(this.tempMandatoryState$),
      filter(([_, tempMandatoryState]) => tempMandatoryState.touched === true),
      delay(300),
      tap(() => this.updatingCustomFieldRequired()),
      switchMap(([customFieldId, tempMandatoryState]) =>
        this.#customFieldsBackendService.setCustomFieldRequired(customFieldId, tempMandatoryState.value).pipe(
          tapResponse(
            (updatedCustomField: WebsiteCustomField) => this.handleCustomFieldRequiredSuccess(updatedCustomField),
            (error: HttpErrorResponse) => this.handleCustomFieldRequiredFailed(error),
            () => this.#adminLoggerService.updateWebsiteCustomFieldMandatory({Mandatory: tempMandatoryState.value})
          ),
        ),
      ),
    ),
  );

  readonly updateCustomFieldRequired = this.effect((request$: Observable<{customFieldId: string, isMandatory: boolean}>) =>
    request$.pipe(
      tap(() => this.updatingCustomFieldRequired()),
      switchMap(({customFieldId, isMandatory}) => this.#customFieldsBackendService.setCustomFieldRequired(customFieldId, isMandatory)),
      tapResponse(
        (updatedCustomField: WebsiteCustomField) => this.handleCustomFieldRequiredSuccess(updatedCustomField),
        (error: HttpErrorResponse) => this.handleCustomFieldRequiredFailed(error),
      ),
    ),
  );

  private updatingCustomFieldRequired = this.updater((state) => ({ ...state, updateRequiredStatus: 'LOADING' }));

  private handleCustomFieldRequiredSuccess = this.updater((state, customField: WebsiteCustomField) => ({
    ...state,
    customField,
    updateRequiredStatus: 'LOADED',
    updateRequiredError: null,
  }));

  private handleCustomFieldRequiredFailed = this.updater((state, updateRequiredError: HttpErrorResponse) => ({
    ...state,
    updateRequiredStatus: 'ERROR',
    updateRequiredError,
  }));

  readonly updateCustomFieldOptions = this.effect((request$: Observable<string>) =>
    request$.pipe(
      withLatestFrom(this.tempOptionsState$),
      filter(([_, tempOptionsState]) => tempOptionsState.touched === true),
      tap(() => this.updatingCustomFieldOptions()),
      switchMap(([customFieldId, tempOptionsState]) =>
        this.#customFieldsBackendService.updateCustomFieldOptions(customFieldId, tempOptionsState.options).pipe(
          tapResponse(
            (customField: WebsiteCustomField) => this.handleUpdateCustomFieldOptionsSuccess(customField),
            (error: HttpErrorResponse) => this.handleUpdateCustomFieldOptionsFailed(error),
            () => this.#adminLoggerService.updateWebsiteCustomFieldValue({})
          ),
        ),
      ),
    ),
  );

  private updatingCustomFieldOptions = this.updater((state) => ({ ...state, updateCustomFieldLoading: 'LOADING' }));

  private handleUpdateCustomFieldOptionsSuccess = this.updater((state, customField: WebsiteCustomField) => ({
    ...state,
    customField,
    updateOptionsStatus: 'LOADED',
    updateOptionsError: null,
  }));

  private handleUpdateCustomFieldOptionsFailed = this.updater((state, updateOptionsError: HttpErrorResponse) => ({
    ...state,
    updateOptionsStatus: 'ERROR',
    updateOptionsError,
  }));

  private getToastContent(
    updateRequiredStatus: LoadStatus,
    updateOptionsStatus: LoadStatus,
    updateRequiredError: HttpErrorResponse | null,
    updateOptionsError: HttpErrorResponse | null,
  ): ToastContent | null {
    if (updateRequiredStatus === 'ERROR' || updateOptionsStatus === 'ERROR') {
      return {
        type: 'error',
        message: this.isDuplicateError(updateOptionsError) ? 'toast.duplicateError' : 'toast.error',
      };
    }
    if (updateRequiredStatus === 'LOADED' || updateOptionsStatus === 'LOADED') {
      return {
        type: 'success',
        message: 'toast.success',
      };
    }
    return null;
  }

  private isDuplicateError(updateOptionsError: HttpErrorResponse | null): boolean {
    //@TODO DATA-519 implement a proper validation
    return updateOptionsError?.error?.errors[0].description === CustomErrors.DuplicateOptions;
  }
}
